import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Plus, MessageCircle, ArrowRight, Star, BadgeCheck } from "lucide-react";
import { http, errMsg, Pill, inputCls, money } from "../lib/market";
import { Eyebrow, GlassCard } from "../components/Section";
import { PriceBreakdown } from "../components/PriceBreakdown";

const QuoteCard = ({ q, onAccept }) => {
  const [agree, setAgree] = useState(false);
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid={`quote-card-${q.quote_id}`}>
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <span className="font-playfair text-lg">{q.lawyer.name}</span>
        {q.lawyer.is_verified && <span className="aj-chip !py-0.5 !text-[9px]"><BadgeCheck className="w-3 h-3" /> Verified</span>}
        <span className="font-inter text-xs text-white/45 inline-flex items-center gap-1"><Star className="w-3 h-3 text-gold-200" /> {q.lawyer.rating} · {q.lawyer.location}</span>
        <span className="ml-auto"><Pill value={q.status} /></span>
      </div>
      <p className="font-inter text-sm text-white/70">{q.scope}</p>
      <p className="font-inter text-xs text-white/45 mt-2">Deliverables: {q.deliverables.join(" · ")} · Est. {q.estimated_days} days · Valid until {q.expires_at.slice(0, 10)}</p>
      <div className="mt-4"><PriceBreakdown s={q.pricing_preview} prefix={`quote-${q.quote_id}`} /></div>
      {q.status === "submitted" && (
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <label className="flex items-start gap-2 font-inter text-xs text-white/60 flex-1">
            <input type="checkbox" className="mt-0.5 accent-[#00C8D7]" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid={`quote-terms-${q.quote_id}`} />
            I accept the scope, fee, timeline and the Standard Engagement Terms v1 (cancellation & refund rules).
          </label>
          <button disabled={!agree} onClick={() => onAccept(q)} className="btn-aj-primary disabled:opacity-40" data-testid={`quote-accept-${q.quote_id}`}>Accept & engage</button>
        </div>
      )}
    </div>
  );
};

const RequestCard = ({ r, onChanged }) => {
  const nav = useNavigate();
  const [quotes, setQuotes] = useState(null);
  const [reply, setReply] = useState("");
  const loadQuotes = async () => setQuotes((await http.get(`/service-requests/${r.request_id}/quotes`)).data.quotes);
  const accept = async (q) => {
    try {
      const { data } = await http.post(`/quotes/${q.quote_id}/accept`, { accept_terms: true });
      toast.success("Engagement created");
      nav(`/engagements/${data.engagement.engagement_id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const sendReply = async () => {
    try { await http.post(`/service-requests/${r.request_id}/clarifications`, { message: reply }); setReply(""); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <GlassCard data-testid={`hub-request-${r.request_id}`}>
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className="aj-chip !py-1 !text-[9px]">{r.category_id.replace(/_/g, " ")}</span>
        {r.funding_route === "justice_wallet" && <span className="aj-chip aj-chip-gold !py-1 !text-[9px]">Justice Wallet</span>}
        {r.allocation && <Pill value={r.allocation.status} testId={`hub-allocation-${r.request_id}`} />}
        <span className="ml-auto"><Pill value={r.status} testId={`hub-request-status-${r.request_id}`} /></span>
      </div>
      <h3 className="font-playfair text-xl">{r.service_name}</h3>
      <p className="font-inter text-xs text-white/45 mt-1">{r.matched_count} lawyer(s) notified · {r.quote_count} offer(s) · {new Date(r.created_at).toLocaleString()}</p>
      {r.clarifications?.length > 0 && (
        <div className="mt-4 space-y-2">
          {r.clarifications.map((c, i) => (
            <div key={i} className={`font-inter text-sm rounded-lg px-3 py-2 ${c.from === "lawyer" ? "bg-gold-400/10 text-gold-200" : "bg-white/5 text-white/70"}`}>
              <MessageCircle className="w-3.5 h-3.5 inline mr-1.5" />{c.from === "lawyer" ? c.lawyer_name : "You"}: {c.message}</div>))}
          <div className="flex gap-2"><input className={inputCls} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply to the lawyer…" data-testid={`hub-reply-input-${r.request_id}`} />
            <button className="btn-aj-secondary" disabled={!reply} onClick={sendReply} data-testid={`hub-reply-btn-${r.request_id}`}>Send</button></div>
        </div>
      )}
      {r.status !== "engaged" && r.quote_count > 0 && !quotes && (
        <button className="btn-aj-secondary mt-4" onClick={loadQuotes} data-testid={`hub-view-quotes-${r.request_id}`}>Review {r.quote_count} offer(s)</button>
      )}
      {quotes && <div className="mt-5 space-y-4">{quotes.map((q) => <QuoteCard key={q.quote_id} q={q} onAccept={accept} />)}</div>}
    </GlassCard>
  );
};

export default function ClientHub() {
  const [tab, setTab] = useState("requests");
  const [reqs, setReqs] = useState(null);
  const [engs, setEngs] = useState([]);
  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([http.get("/service-requests/mine"), http.get("/engagements")]);
      setReqs(a.data.requests); setEngs(b.data.engagements);
    } catch (e) { toast.error(errMsg(e)); setReqs([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <main className="bg-ink text-white min-h-screen py-12" data-testid="client-hub-page">
      <div className="container mx-auto px-5 sm:px-8 max-w-6xl">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div><Eyebrow>Client Dashboard</Eyebrow><h1 className="font-playfair text-4xl sm:text-5xl mt-3">My <span className="italic text-aj-gradient">matters.</span></h1></div>
          <div className="flex gap-3"><Link to="/my-requests" className="btn-aj-ghost text-xs" data-testid="hub-litigation-link">Litigation cases</Link>
            <Link to="/request-service" className="btn-aj-primary" data-testid="hub-new-request"><Plus className="w-4 h-4" /> New request</Link></div>
        </div>
        <div className="flex gap-2 mb-8">
          {[["requests", `Requests (${reqs?.length || 0})`], ["engagements", `Engagements (${engs.length})`]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`hub-tab-${k}`}
              className={`px-4 py-2 rounded-full font-montserrat text-[11px] uppercase tracking-wider transition-colors ${tab === k ? "bg-turquoise text-ink font-semibold" : "border border-white/15 text-white/70 hover:text-white"}`}>{l}</button>))}
        </div>
        {!reqs && <Loader2 className="w-5 h-5 animate-spin text-turquoise" />}
        {tab === "requests" && reqs && (
          <div className="space-y-4">
            {reqs.length === 0 && <GlassCard className="text-center !py-12" data-testid="hub-empty"><p className="font-inter text-white/55 mb-5">No service requests yet.</p><Link to="/services" className="btn-aj-primary">Browse services</Link></GlassCard>}
            {reqs.map((r) => <RequestCard key={r.request_id} r={r} onChanged={load} />)}
          </div>
        )}
        {tab === "engagements" && (
          <div className="space-y-3">
            {engs.map((e, i) => (
              <motion.div key={e.engagement_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                <Link to={`/engagements/${e.engagement_id}`} data-testid={`hub-engagement-${e.engagement_id}`}
                  className="glass rounded-2xl p-5 flex flex-wrap items-center gap-4 border border-white/8 hover:border-turquoise/40 transition-colors">
                  <div className="flex-1 min-w-[200px]"><div className="font-playfair text-lg">{e.service_name}</div>
                    <div className="font-inter text-xs text-white/45">{e.lawyer_name} · {e.engagement_id}</div></div>
                  <div className="font-montserrat text-gold-200">{money(e.pricing_snapshot.gross_minor, e.currency)}</div>
                  <Pill value={e.status} /><Pill value={e.payment?.state} /><ArrowRight className="w-4 h-4 text-white/40" />
                </Link>
              </motion.div>))}
          </div>
        )}
      </div>
    </main>
  );
}
